import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Row } from "react-bootstrap";
import styled from "styled-components";

import { useMap } from "common/hooks/useMap";
import AuthContext from "common/store/AuthContext";
import StatusContext from "common/store/StatusContext";

import { BaseForm } from "common/components/templates/BaseForm";
import { DashboardTemplate } from "common/components/templates/DashboardTemplate";
import { Map2D } from "common/components/map/Map2D";
import { CURSOR } from "common/components/map/common/osm/components/CursorDrawer";
import { PolygonDrawer } from "common/components/map/common/osm/components/PolygonDrawer";
import { PolygonViewSettings } from "common/components/map/common/osm/components/PolygonViewSettings";
import { SFilterCheck, SFilterSection } from "./components/MapFilterControls";
import { MapTopPanel } from "./components/MapTopPanel";

import { createDocumentTitle } from "utils/commonTools";
import { useInquiry } from "pages/inquiry/hooks/useInquiry";
import { InquiryDetailTable } from "pages/inquiry/components/InquiryDetailTable";
import { InquiryData } from "pages/inquiry/types/InquiryInfo";
import { useMapInquiry } from "./hooks/useMapInquiry";
import {
  ChartOverlayData,
  ChartOverlayDrawer,
} from "common/components/map/common/osm/components/ChartOverlayDrawer";
import { ChartViewSettings } from "common/components/map/common/osm/components/ChartViewSettings";
import { Coordinates, PolygonArea } from "common/components/map/common/types/MapData";
import { NameID } from "common/types/NameID";
import { InquiryMeta } from "pages/inquiry/types/InquiryMeta";
import { ReportData } from "./types/ReportData";
import SearchContext from "common/store/SearchContext";

export const MapInquiryPage = ({ pageTitle }: { pageTitle: string }) => {
  const isInitializedRef = useRef(false);
  const { selectedPark } = useContext(AuthContext);
  const { searchFrom, searchTo } = useContext(SearchContext);
  const { setIsLoading, setLoadingMessage, setErrorMessage } = useContext(StatusContext);

  const { loadMetaData } = useInquiry();
  const { buildAreaPolygons } = useMap();
  const { searchData, convertToOverlays } = useMapInquiry();

  const [metaData, setMetaData] = useState<InquiryMeta>();
  const [polygons, setPolygons] = useState<PolygonArea[]>([]);
  const [chartData, setChartData] = useState<ChartOverlayData[]>([]);
  const [reportData, setReportData] = useState<ReportData<InquiryData>[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<NameID[]>([]);
  const [selectedAreaId, setSelectedAreaId] = useState<number>();
  const [selectedPin, setSelectedPin] = useState<Coordinates>();
  const [selectedDetails, setSelectedDetails] = useState<InquiryData[] | null>(null);
  // テーブルに日付を追加するためのもの
  const [detailsDates, setDetailsDates] = useState<Date[] | null>(null);
  const [showCharts, setShowCharts] = useState(true);
  const [showPins, setShowPins] = useState(true);
  const [showPolygons, setShowPolygons] = useState(true);
  const [showPolygonLabels, setShowPolygonLabels] = useState(false);

  const initialize = async () => {
    if (!selectedPark) return;

    isInitializedRef.current = true;

    const meta = await loadMetaData(selectedPark.parkId);
    if (!meta.succeeded) {
      setErrorMessage(`メタデータの取得に失敗しました。${meta.msg}`);
      return;
    }
    setMetaData(meta.data);
    setPolygons(meta.data ? buildAreaPolygons(meta.data.areas) : []);
    setSelectedCategories(meta.data?.categories.map((x) => x.category) ?? []);
    await handleSearch(searchFrom, searchTo, meta.data?.areas);
  };

  useEffect(() => {
    document.title = createDocumentTitle(pageTitle);
    if (isInitializedRef.current) return;

    const f = async () => initialize();
    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  /**
   * 検索処理を実行する
   */
  const handleSearch = async (from: Date, to: Date, areasOverride?: InquiryMeta["areas"]) => {
    if (!selectedPark) {
      return;
    }

    setErrorMessage("");
    setIsLoading(true);
    setLoadingMessage("データの検索中");

    const res = await searchData(selectedPark.parkId, from, to);

    setIsLoading(false);
    if (!res.succeeded) {
      setErrorMessage(res.msg || "");
      return;
    }
    const resData = res.data ?? [];
    setReportData(resData);
    setSelectedAreaId(undefined);
    setSelectedPin(undefined);

    const areas = areasOverride ?? metaData?.areas;
    if (!areas || areas.length === 0) setChartData([]);
    else setChartData(convertToOverlays(resData, areas));
  };

  /**
   *グラフのクリック
   */
  const handleBarSelected = (areaId: number, pin?: Coordinates) => {
    setSelectedAreaId(areaId);
    setSelectedPin(pin);
  };

  const handleBarDeselected = () => {
    setSelectedAreaId(undefined);
    setSelectedPin(undefined);
  };

  /**
   * フィルターの変更
   */
  const toggleCategory = (category: NameID, checked: boolean) => {
    setSelectedCategories((prev) => {
      if (checked) {
        if (prev.some((x) => x.id === category.id)) return prev;
        return [...prev, category];
      }
      return prev.filter((x) => x.id !== category.id);
    });
  };

  /**
   * 全てチェックの場合
   */
  const toggleAllCategories = (checked: boolean) => {
    if (checked) {
      setSelectedCategories(filterOptions);
    } else {
      setSelectedCategories([]);
    }
  };

  const matchArea = (data: InquiryData, areaId: number) => {
    if (areaId >= 0) {
      if (data.areas?.some((x) => x.id === areaId)) return true;
      return data.point?.area?.id === areaId;
    }

    if (areaId === -1) {
      return !data.areas?.length && !data.point;
    }

    if (areaId === -2) {
      return !!data.point && !data.point.area;
    }

    return false;
  };

  useEffect(() => {
    let matchedRows =
      selectedAreaId !== undefined
        ? reportData.filter((x) => matchArea(x.value, selectedAreaId))
        : reportData;
    if (selectedPin) {
      matchedRows = matchedRows.filter(
        (x) =>
          x.value.point?.coords.x === selectedPin.x && x.value.point?.coords.y === selectedPin.y,
      );
    }
    const details = matchedRows
      .map((x) => x.value)
      .filter((x) =>
        x.category ? selectedCategories.find((y) => y.id === x.category?.id) : false,
      );
    const dates = matchedRows
      .filter((x) =>
        x.value.category ? selectedCategories.find((y) => y.id === x.value.category?.id) : false,
      )
      .map((x) => x.date);

    setSelectedDetails(details);
    setDetailsDates(dates);
  }, [selectedAreaId, selectedPin, reportData, selectedCategories]);

  // フィルターの項目
  const filterOptions = useMemo(() => {
    return metaData?.categories.map((x) => x.category) ?? [];
  }, [metaData?.categories]);

  const filteredChartData = useMemo(
    () =>
      chartData
        .map((x) => ({
          ...x,
          bars: x.bars.filter((b) => selectedCategories.find((y) => y.id === b.label.id)),
        }))
        .filter((x) => x.bars.length > 0),
    [chartData, selectedCategories],
  );

  const selectedArea = useMemo(
    () => metaData?.areas.find((x) => x.area.id === selectedAreaId)?.area,
    [metaData?.areas, selectedAreaId],
  );

  const allChecked =
    filterOptions.length > 0 &&
    filterOptions.every((x) => selectedCategories.find((y) => y.id === x.id));

  return (
    <DashboardTemplate>
      <MapTopPanel pageTitle={pageTitle} onSearch={handleSearch} />

      <BaseForm className="p-3">
        <SMapForm>
          <SMapPane>
            <Map2D
              cursorType={CURSOR.NONE}
              mapFunctions={[
                <PolygonDrawer
                  key="polygons"
                  polygons={polygons}
                  showPolygons={showPolygons}
                  showLabels={showPolygonLabels}
                />,
                <ChartOverlayDrawer
                  key="charts"
                  chartData={filteredChartData}
                  showCharts={showCharts}
                  showPins={showPins}
                  onBarSelected={handleBarSelected}
                  onBarDeselected={handleBarDeselected}
                />,
                ...(polygons.length > 0
                  ? [
                      <PolygonViewSettings
                        key="polygon-view-settings"
                        showPolygons={showPolygons}
                        onShowPolygonsChange={setShowPolygons}
                        showLabels={showPolygonLabels}
                        onShowLabelsChange={setShowPolygonLabels}
                      />,
                    ]
                  : []),
                ...(filteredChartData.length > 0
                  ? [
                      <ChartViewSettings
                        key="chart-view-settings"
                        showCharts={showCharts}
                        onShowChartsChange={setShowCharts}
                        showPins={showPins}
                        onShowPinsChange={setShowPins}
                      />,
                    ]
                  : []),
              ]}
            />
          </SMapPane>
          <SFilterSection>
            <SFilterCheck
              type="checkbox"
              label="全て表示"
              $checked={allChecked}
              $isPrimary={true}
              checked={allChecked}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                toggleAllCategories(e.currentTarget.checked)
              }
            />
            {filterOptions.map((x) => (
              <SFilterCheck
                key={x.id}
                type="checkbox"
                label={x.name}
                $checked={selectedCategories.some((y) => y.id === x.id)}
                checked={selectedCategories.some((y) => y.id === x.id)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  toggleCategory(x, e.currentTarget.checked)
                }
              />
            ))}
          </SFilterSection>
        </SMapForm>

        <SDetailSection>
          {selectedArea && <SDetailHeader>{`内訳: ${selectedArea.name}`}</SDetailHeader>}
          {metaData && selectedDetails !== null && (
            <SDetailTableWrapper>
              <InquiryDetailTable
                Details={selectedDetails}
                Meta={metaData}
                Dates={detailsDates ?? undefined}
                readonly={true}
                addDataRequested={() => {}}
                editDataRequested={(_data, _index) => {}}
                deleteDataRequested={(_data) => {}}
                showTitle={false}
                showNumber={true}
              />
            </SDetailTableWrapper>
          )}
        </SDetailSection>
      </BaseForm>
    </DashboardTemplate>
  );
};

const SMapForm = styled(Row)`
  flex: 1;
  height: 40vh;
  display: flex;
  flex-direction: column;
`;

const SMapPane = styled.div`
  flex: 1 1 0;
  min-height: 0;
`;

const SDetailSection = styled.div`
  margin-top: 0.75rem;
`;

const SDetailTableWrapper = styled.div`
  max-height: 38vh;
  overflow: auto;

  .table-responsive {
    overflow: visible;
  }

  table {
    margin-bottom: 0;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: hsl(200, 60%, 85%);
  }
`;

const SDetailHeader = styled.div`
  font-weight: 600;
`;
